import { useCallback, useEffect, useRef, useState } from 'react';

// Only a leading slash command is eligible. Ordinary prose, paths, and goal
// control commands must keep their normal send behavior.
export function getGoalCommandPrompt(value) {
  const text = String(value || '').trimStart();
  const command = text.trimEnd().toLowerCase();
  if (command && '/goal'.startsWith(command)) {
    return command === '/goal' && /\s$/.test(text) ? 'objective' : 'command';
  }
  if (command === '/goal set') return 'objective';
  return null;
}

export function isGoalCommand(value) {
  return /^\/goal(?:\s|$)/i.test(String(value || '').trimStart());
}

export default function useGoalCommand({ value, contextId, enabled, onChangeText, onSelectWorkMode }) {
  const inputRef = useRef(null);
  const [dismissed, setDismissed] = useState(null);
  const [editing, setEditing] = useState(false);
  const prompt = enabled ? getGoalCommandPrompt(value) : null;
  const visible = !!prompt && !(dismissed?.value === value && dismissed?.contextId === contextId);
  useEffect(() => setDismissed(null), [value, contextId]);
  useEffect(() => setEditing(false), [contextId]);

  const select = useCallback(() => {
    if (onSelectWorkMode?.() === false) return;
    setDismissed(null);
    setEditing(true);
  }, [onSelectWorkMode]);

  const dismiss = useCallback(() => setDismissed({ value, contextId }), [value, contextId]);
  const cancelEditing = useCallback(() => {
    setEditing(false);
    if (getGoalCommandPrompt(value)) onChangeText('');
    inputRef.current?.focus();
  }, [value, onChangeText]);
  const finishEditing = useCallback(() => setEditing(false), []);

  // Both the send button and keyboard shortcuts go through this path, including
  // pasted commands that haven't produced a React render yet.
  const prepareSend = useCallback((text) => {
    if (!enabled) return 'message';
    if (getGoalCommandPrompt(text)) {
      select(text);
      return 'compose';
    }
    if (isGoalCommand(text)) {
      if (onSelectWorkMode?.() === false) return 'compose';
      return 'goal';
    }
    return 'message';
  }, [enabled, select, onSelectWorkMode]);

  const onKeyPress = useCallback((event) => {
    if (editing || !visible || event.isComposing || event.nativeEvent?.isComposing) return false;
    const key = event.key || event.nativeEvent?.key;
    if (key === 'Escape') {
      event.preventDefault?.();
      event.stopPropagation?.();
      dismiss();
      return true;
    }
    if (key === 'Tab' && prompt === 'command' && !event.shiftKey && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault?.();
      select();
      return true;
    }
    return false;
  }, [editing, visible, prompt, dismiss, select]);

  return { inputRef, prompt: !editing && visible ? prompt : null, editing, cancelEditing, finishEditing,
    select, dismiss, onKeyPress, prepareSend };
}
